import {
  Box2d,
  Box2dModel,
  TLDefaultHorizontalAlignStyle,
  TLDefaultVerticalAlignStyle,
  toDomPrecision,
} from '@tldraw/tldraw'
import { TextBoxShapeProps } from './Types'
import { FONT_WEIGHTS, TEXT_PROPS } from './constants'
import { reportError } from 'utils/error'
import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { TextManager } from 'components/VisualStudioPlayground/VisualEditor/TextManager'

export function getTextSize(textManager: TextManager, props: TextBoxShapeProps) {
  let { uppercase, italic, fontWeight, fontFamily, text, autoSize, fontSize, w, lineHeight, letterSpacing } = props

  const minWidth = autoSize ? 16 : Math.max(16, w)
  const maxWidth = autoSize
    ? null
    : // `measureText` floors the number so we need to do the same here to avoid issues.
      Math.floor(Math.max(minWidth, w))

  if (uppercase) {
    text = text.toUpperCase()
  }

  const result = textManager.measureText(text, {
    ...TEXT_PROPS,
    fontFamily,
    fontSize,
    fontWeight: `${fontWeight}`,
    fontStyle: italic ? 'italic' : 'inherit',
    maxWidth,
    lineHeight,
    letterSpacing,
    fontVariant: getCSSFontVariant(fontFamily),
  })

  // If we're autosizing the measureText will essentially `Math.floor`
  // the numbers so `19` rather than `19.3`, this means we must +1 to
  // whatever we get to avoid wrapping.
  if (autoSize) {
    result.w += 1
  }

  return {
    width: Math.max(minWidth, result.w),
    height: Math.max(fontSize, result.h),
  }
}

function correctSpacesToNbsp(input: string) {
  return input.replace(/\s/g, '\xa0')
}

// Taken as-is from tl-draw and adapted to our style props
// https://github.com/tldraw/tldraw/blob/4b4c258219eafdf36d7a1b9299478eac9459b38a/packages/tldraw/src/lib/shapes/shared/createTextSvgElementFromSpans.ts#L14
export function createTextSvgElementFromSpans(
  spans: { text: string; box: Box2dModel }[],
  opts: {
    fontSize: number
    fontFamily: string
    textAlign: TLDefaultHorizontalAlignStyle
    verticalTextAlign: TLDefaultVerticalAlignStyle
    fontWeight: string
    fontStyle: string
    lineHeight: number
    letterSpacing: number
    width: number
    height: number
    color: string
    padding?: number
    offsetX?: number
    offsetY?: number
    textDecoration: string
    textTransform: string
    strokeWidth?: number
    strokeColor?: string
    ascender: number
    unitsPerEm: number
  },
) {
  const { padding = 0 } = opts

  // Create the text element
  const textElm = document.createElementNS('http://www.w3.org/2000/svg', 'text')
  textElm.setAttribute('font-size', opts.fontSize + 'px')
  textElm.setAttribute('font-family', `"${opts.fontFamily}", "Noto Color Emoji"`)
  textElm.setAttribute('font-style', opts.fontStyle)
  textElm.setAttribute('font-weight', opts.fontWeight)
  textElm.setAttribute('line-height', opts.lineHeight * opts.fontSize + 'px')
  textElm.setAttribute('letter-spacing', toDomPrecision(opts.letterSpacing / 1000) + 'em')
  textElm.setAttribute('dominant-baseline', 'mathematical')
  textElm.setAttribute('alignment-baseline', 'mathematical')
  textElm.setAttribute('text-decoration', opts.textDecoration)
  textElm.setAttribute('text-transform', opts.textTransform)
  textElm.setAttribute('fill', opts.color)

  if (opts.strokeWidth && opts.strokeColor) {
    textElm.setAttribute('stroke', opts.strokeColor)
    textElm.setAttribute('stroke-width', opts.strokeWidth.toString())
  }

  if (spans.length === 0) return textElm

  const bounds = Box2d.From(spans[0].box)
  for (const { box } of spans) {
    bounds.union(box)
  }

  const offsetX = padding + (opts.offsetX ?? 0)
  const offsetY = ((opts.ascender / opts.unitsPerEm) * opts.fontSize) / 2

  // Create text span elements for each word
  let currentLineTop = null
  for (const { text, box } of spans) {
    // if we broke a line, add a line break span. This helps tools like
    // figma import our exported svg correctly
    const didBreakLine = currentLineTop !== null && box.y > currentLineTop
    if (didBreakLine) {
      const lineBreakTspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan')
      lineBreakTspan.setAttribute('alignment-baseline', 'mathematical')
      lineBreakTspan.setAttribute('x', offsetX + 'px')
      lineBreakTspan.setAttribute('y', box.y + offsetY + 'px')
      lineBreakTspan.textContent = '\n'
      textElm.appendChild(lineBreakTspan)
    }

    const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan')
    tspan.setAttribute('alignment-baseline', 'mathematical')
    tspan.setAttribute('x', box.x + offsetX + 'px')
    tspan.setAttribute('y', box.y + offsetY + 'px')
    const cleanText = correctSpacesToNbsp(text)
    tspan.textContent = cleanText
    textElm.appendChild(tspan)

    currentLineTop = box.y
  }

  return textElm
}

export async function getFontAsBase64(url: string) {
  const response = await fetch(url)
  const clonedResponse = response.clone()
  let text = await response.text()

  // Check if the response is a CSS file
  if (text.includes('@font-face')) {
    // Find all URLs in the CSS file
    const urlRegex = /url\(([^)]+)\)/g
    let match
    while ((match = urlRegex.exec(text)) !== null) {
      const fontUrl = match[1]

      try {
        // Recursively process the URL
        const base64Font = await getFontAsBase64(fontUrl)
        // Replace the URL with the base64 encoded font
        text = text.replace(fontUrl, `data:;base64,${base64Font}`)
      } catch (e) {
        reportError(e)
      }
    }
  } else {
    // Convert the response to a base64 string
    const arrayBuffer = await clonedResponse.arrayBuffer()
    const blob = new Blob([arrayBuffer])

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1]
        resolve(base64String)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  return text
}

export function sanitizeFontWeight(newFontWeight: string | number, prevFontWeight: FontWeightType): FontWeightType {
  const parsedWeight = Number.parseInt(newFontWeight.toString(), 10)

  if (Object.keys(FONT_WEIGHTS).map(Number).includes(parsedWeight)) {
    return parsedWeight as FontWeightType
  } else {
    return prevFontWeight
  }
}

export function sanitizeFontSize(newFontSize: string | number, prevFontSize: number): number {
  const parsedSize = Number.parseInt(newFontSize.toString(), 10)

  if (parsedSize > 0) {
    return parsedSize
  } else {
    return prevFontSize
  }
}

export function sanitizeText(text: string) {
  return text.replace(/[\p{Zs}\p{Zl}]+/gu, ' ')
}

export function getCSSFontVariant(fontFamily: string) {
  if (fontFamily === 'Anton SC') {
    return 'none'
  }

  return 'normal'
}
